import type { ScoreInput } from '../index.js'

const ONE_DECIMAL = 10
const PERCENT = 100

function isAnchored(input: ScoreInput): input is ScoreInput & { best: number; worst: number } {
  return typeof input.best === 'number' && typeof input.worst === 'number' && input.best !== input.worst
}

const toOneDecimal = (value: number): number => Math.round(value * ONE_DECIMAL) / ONE_DECIMAL

/** An anchor in the terms the count beside it uses: a share of shifts as a percent, a per-period behavior as a plain count. */
function formatAnchor(anchor: number, input: ScoreInput): string {
  return input.denominator === null ? String(toOneDecimal(anchor)) : `${toOneDecimal(anchor * PERCENT)}%`
}

/**
 * What a behavior's 0 to 100 is read against, in words: "Full marks at 0%,
 * none at 20% or more" for one where less is better, "Full marks at 100%,
 * none at 70% or less" for one where more is. Without it a 0 beside
 * "On time for 2 of 4 shifts worked" reads as a mistake. Null for a breakdown that carries no
 * anchors (imported, or computed before they were added).
 */
export function describeAnchors(input: ScoreInput): string | null {
  if (!isAnchored(input)) {
    return null
  }
  const beyond = input.best < input.worst ? 'more' : 'less'
  // A count cannot go below none, so "none at 0 or less" would say less than it means.
  const worstSide = input.denominator === null && input.worst <= 0 ? '' : ` or ${beyond}`
  return `Full marks at ${formatAnchor(input.best, input)}, none at ${formatAnchor(input.worst, input)}${worstSide}`
}
